// First Created - 19 Aug 2026
// Author - Atul Pandey
// Purpose - To create an Org Trail Session Challenge Assignment
//           for all or selected users within a cohort

import React, {
    useEffect,
    useMemo,
    useState,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import {
    useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"

/*
|--------------------------------------------------------------------------
| Adjust only this import path if your Sessions RTK slice lives elsewhere.
|--------------------------------------------------------------------------
*/

import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"

import {
    useGetChallengesQuery,
} from "@/features/challenges/challengesApiSlice"

import {
    useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"

import {
    useGetOrgTrailSessionChallengeAssignmentsQuery,
    useUpsertOrgTrailSessionChallengeAssignmentMutation,
} from "./orgTrailSessionChallengeAssignmentsApiSlice"

/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getRefId = (
    value
) => {
    if (
        !value
    ) {
        return ""
    }

    if (
        typeof value ===
        "string"
    ) {
        return value
    }

    return (
        value?._id ||
        value?.id ||
        ""
    )
}

const getDisplayName = (
    user
) => {
    return (
        user?.name ||
        user?.username ||
        user?.email ||
        "User"
    )
}

/*
|--------------------------------------------------------------------------
| Component
|--------------------------------------------------------------------------
*/

const EtCreateOrgTrailSessionChallengeAssignment = ({
    setActiveAdminView,
}) => {

    // First Created - 19 Aug 2026
    // Author - Atul Pandey
    // Purpose - Create explicit Session Challenge assignment / metering

    useTitle(
        "Create Session Challenge Assignment"
    )

    const navigate =
        useNavigate()

    /*
    |--------------------------------------------------------------------------
    | 02 - Initial Form State
    |--------------------------------------------------------------------------
    */

    const initialFormState = {
        orgId:
            "",

        trailId:
            "",

        cohortId:
            "",

        sessionId:
            "",

        challengeId:
            "",

        audienceType:
            "AllCohortUsers",

        selectedUserIds:
            [],

        assignmentStatus:
            "Open",

        availableFrom:
            "",

        availableUntil:
            "",

        assignmentReason:
            "",

        active:
            true,
    }

    const [
        formData,
        setFormData,
    ] =
        useState(
            initialFormState
        )

    /*
    |--------------------------------------------------------------------------
    | 03 - Create / Upsert Mutation
    |--------------------------------------------------------------------------
    */

    const [
        upsertOrgTrailSessionChallengeAssignment,
        {
            isLoading,
            isSuccess,
            isError,
            error,
        },
    ] =
        useUpsertOrgTrailSessionChallengeAssignmentMutation()

    /*
    |--------------------------------------------------------------------------
    | 04 - Organizations
    |--------------------------------------------------------------------------
    */

    const {
        data:
            organizations,

        isSuccess:
            isOrganizationsSuccess,
    } =
        useGetOrganizationsQuery(
            "organizationsList",
            {
                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 05 - Trails
    |--------------------------------------------------------------------------
    */

    const {
        data:
            trails,

        isSuccess:
            isTrailsSuccess,
    } =
        useGetTrailsQuery(
            "trailsList",
            {
                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 06 - Sessions
    |--------------------------------------------------------------------------
    */

    const {
        data:
            sessions,

        isSuccess:
            isSessionsSuccess,
    } =
        useGetSessionsQuery(
            "sessionsList",
            {
                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 07 - Challenges
    |--------------------------------------------------------------------------
    */

    const {
        data:
            challenges,

        isSuccess:
            isChallengesSuccess,
    } =
        useGetChallengesQuery()

    /*
    |--------------------------------------------------------------------------
    | 08 - Cohorts
    |--------------------------------------------------------------------------
    |
    | Existing cohort data is used as the authoritative source of
    | cohort membership.
    |
    |--------------------------------------------------------------------------
    */

    const {
        data:
            cohortsData,

        isSuccess:
            isCohortsSuccess,
    } =
        useGetOrgTrailSessionCohortsQuery({
            active:
                true,
        })

    /*
    |--------------------------------------------------------------------------
    | 09 - Existing Assignments
    |--------------------------------------------------------------------------
    |
    | Used to prevent creating a duplicate explicit assignment for the same:
    |
    | org + trail + session + challenge + cohort
    |
    |--------------------------------------------------------------------------
    */

    const {
        data:
            existingAssignmentsData,

        isSuccess:
            isAssignmentsSuccess,
    } =
        useGetOrgTrailSessionChallengeAssignmentsQuery({
            active:
                true,
        })

    /*
    |--------------------------------------------------------------------------
    | 10 - Return To List After Success
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                isSuccess
            ) {
                setFormData(
                    initialFormState
                )

                if (
                    setActiveAdminView
                ) {
                    setActiveAdminView(
                        "orgTrailSessionChallengeAssignments"
                    )
                } else {
                    navigate(
                        "/orgTrailSessionChallengeAssignments"
                    )
                }
            }
        },
        [
            isSuccess,
            navigate,
            setActiveAdminView,
        ]
    )

    /*
    |--------------------------------------------------------------------------
    | 11 - Normalize Organizations
    |--------------------------------------------------------------------------
    */

    const organizationList =
        useMemo(
            () => {
                if (
                    !isOrganizationsSuccess ||
                    !organizations
                        ?.ids
                ) {
                    return []
                }

                return organizations.ids
                    .map(
                        id =>
                            organizations
                                .entities[
                                id
                            ]
                    )
                    .filter(
                        Boolean
                    )
            },
            [
                isOrganizationsSuccess,
                organizations,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 12 - Normalize Trails
    |--------------------------------------------------------------------------
    */

    const trailList =
        useMemo(
            () => {
                if (
                    !isTrailsSuccess ||
                    !trails?.ids
                ) {
                    return []
                }

                return trails.ids
                    .map(
                        id =>
                            trails
                                .entities[
                                id
                            ]
                    )
                    .filter(
                        Boolean
                    )
            },
            [
                isTrailsSuccess,
                trails,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 13 - Normalize Sessions
    |--------------------------------------------------------------------------
    */

    const sessionList =
        useMemo(
            () => {
                if (
                    !isSessionsSuccess ||
                    !sessions
                        ?.ids
                ) {
                    return []
                }

                return sessions.ids
                    .map(
                        id =>
                            sessions
                                .entities[
                                id
                            ]
                    )
                    .filter(
                        Boolean
                    )
            },
            [
                isSessionsSuccess,
                sessions,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 14 - Normalize Challenges
    |--------------------------------------------------------------------------
    */

    const challengeList =
        useMemo(
            () => {
                if (
                    !isChallengesSuccess ||
                    !challenges
                        ?.ids
                ) {
                    return []
                }

                return challenges.ids
                    .map(
                        id =>
                            challenges
                                .entities[
                                id
                            ]
                    )
                    .filter(
                        Boolean
                    )
            },
            [
                isChallengesSuccess,
                challenges,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 15 - Normalize Cohorts
    |--------------------------------------------------------------------------
    */

    const cohortList =
        useMemo(
            () => {
                if (
                    !isCohortsSuccess ||
                    !cohortsData
                        ?.ids
                ) {
                    return []
                }

                return cohortsData.ids
                    .map(
                        id =>
                            cohortsData
                                .entities[
                                id
                            ]
                    )
                    .filter(
                        Boolean
                    )
            },
            [
                isCohortsSuccess,
                cohortsData,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 16 - Normalize Existing Assignments
    |--------------------------------------------------------------------------
    */

    const existingAssignmentList =
        useMemo(
            () => {
                if (
                    !isAssignmentsSuccess ||
                    !existingAssignmentsData
                        ?.ids
                ) {
                    return []
                }

                return existingAssignmentsData.ids
                    .map(
                        id =>
                            existingAssignmentsData
                                .entities[
                                id
                            ]
                    )
                    .filter(
                        Boolean
                    )
            },
            [
                isAssignmentsSuccess,
                existingAssignmentsData,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 17 - Filter Cohorts For Selected Org + Trail
    |--------------------------------------------------------------------------
    */

    const filteredCohorts =
        useMemo(
            () => {
                if (
                    !formData.orgId ||
                    !formData.trailId
                ) {
                    return []
                }

                return cohortList
                    .filter(
                        cohort =>
                            getRefId(
                                cohort
                                    ?.orgId
                            ) ===
                                formData
                                    .orgId &&
                            getRefId(
                                cohort
                                    ?.trailId
                            ) ===
                                formData
                                    .trailId &&
                            cohort
                                ?.active !==
                                false
                    )
                    .sort(
                        (
                            a,
                            b
                        ) => {
                            const seqA =
                                a
                                    ?.cohortSequence ||
                                999999

                            const seqB =
                                b
                                    ?.cohortSequence ||
                                999999

                            if (
                                seqA !==
                                seqB
                            ) {
                                return (
                                    seqA -
                                    seqB
                                )
                            }

                            return (
                                a
                                    ?.cohortName ||
                                ""
                            )
                                .localeCompare(
                                    b
                                        ?.cohortName ||
                                    ""
                                )
                        }
                    )
            },
            [
                cohortList,
                formData.orgId,
                formData.trailId,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 18 - Filter Sessions For Selected Trail
    |--------------------------------------------------------------------------
    */

    const filteredSessions =
        useMemo(
            () => {
                if (
                    !formData.trailId
                ) {
                    return []
                }

                return sessionList
                    .filter(
                        session =>
                            getRefId(
                                session
                                    ?.trailId
                            ) ===
                                formData
                                    .trailId &&
                            session
                                ?.active !==
                                false
                    )
                    .sort(
                        (
                            a,
                            b
                        ) => {
                            const seqA =
                                a
                                    ?.sessionSequence ||
                                999999

                            const seqB =
                                b
                                    ?.sessionSequence ||
                                999999

                            return (
                                seqA -
                                seqB
                            )
                        }
                    )
            },
            [
                sessionList,
                formData.trailId,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 19 - Selected Session
    |--------------------------------------------------------------------------
    */

    const selectedSession =
        useMemo(
            () => {
                return filteredSessions
                    .find(
                        session =>
                            getRefId(
                                session
                            ) ===
                            formData
                                .sessionId
                    ) ||
                    null
            },
            [
                filteredSessions,
                formData.sessionId,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 20 - Challenges Attached To Selected Session
    |--------------------------------------------------------------------------
    |
    | Do NOT show every Challenge in Trinity.
    |
    | Only challenges already attached to:
    |
    | Session.sessionChallenges[]
    |
    |--------------------------------------------------------------------------
    */

    const filteredSessionChallenges =
        useMemo(
            () => {
                if (
                    !selectedSession
                ) {
                    return []
                }

                const sessionChallengeIds =
                    (
                        selectedSession
                            ?.sessionChallenges ||
                        []
                    )
                        .map(
                            item =>
                                getRefId(
                                    item
                                        ?.challengeId
                                )
                        )
                        .filter(
                            Boolean
                        )

                return challengeList
                    .filter(
                        challenge =>
                            sessionChallengeIds
                                .includes(
                                    getRefId(
                                        challenge
                                    )
                                )
                    )
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            (
                                a
                                    ?.challengeName ||
                                ""
                            )
                                .localeCompare(
                                    b
                                        ?.challengeName ||
                                    ""
                                )
                    )
            },
            [
                selectedSession,
                challengeList,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 21 - Selected Cohort
    |--------------------------------------------------------------------------
    */

    const selectedCohort =
        useMemo(
            () => {
                return filteredCohorts
                    .find(
                        cohort =>
                            getRefId(
                                cohort
                            ) ===
                            formData
                                .cohortId
                    ) ||
                    null
            },
            [
                filteredCohorts,
                formData.cohortId,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 22 - Eligible Users From Selected Cohort Only
    |--------------------------------------------------------------------------
    */

    const eligibleCohortUsers =
        useMemo(
            () => {
                if (
                    !selectedCohort
                ) {
                    return []
                }

                return (
                    selectedCohort
                        ?.cohortUserIds ||
                    []
                )
                    .filter(
                        item =>
                            item
                                ?.userId &&
                            item
                                ?.active !==
                                false &&
                            ![
                                "Dropped",
                                "Archived",
                                "Transferred",
                            ].includes(
                                item
                                    ?.userCohortStatus
                            )
                    )
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            getDisplayName(
                                a?.userId
                            )
                                .localeCompare(
                                    getDisplayName(
                                        b
                                            ?.userId
                                    )
                                )
                    )
            },
            [
                selectedCohort,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 23 - Duplicate Assignment Check
    |--------------------------------------------------------------------------
    */

    const matchingExistingAssignment =
        useMemo(
            () => {
                if (
                    !formData.orgId ||
                    !formData.trailId ||
                    !formData.sessionId ||
                    !formData.challengeId ||
                    !formData.cohortId
                ) {
                    return null
                }

                return (
                    existingAssignmentList
                        .find(
                            assignment =>
                                getRefId(
                                    assignment
                                        ?.orgId
                                ) ===
                                    formData
                                        .orgId &&
                                getRefId(
                                    assignment
                                        ?.trailId
                                ) ===
                                    formData
                                        .trailId &&
                                getRefId(
                                    assignment
                                        ?.sessionId
                                ) ===
                                    formData
                                        .sessionId &&
                                getRefId(
                                    assignment
                                        ?.challengeId
                                ) ===
                                    formData
                                        .challengeId &&
                                getRefId(
                                    assignment
                                        ?.cohortId
                                ) ===
                                    formData
                                        .cohortId
                        ) ||
                    null
                )
            },
            [
                existingAssignmentList,
                formData.orgId,
                formData.trailId,
                formData.sessionId,
                formData.challengeId,
                formData.cohortId,
            ]
        )

    /*
    |--------------------------------------------------------------------------
    | 24 - Generic Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {
        const {
            name,
            value,
            type,
            checked,
        } =
            e.target

        if (
            type ===
            "checkbox"
        ) {
            setFormData(
                prev => ({
                    ...prev,

                    [name]:
                        checked,
                })
            )

            return
        }

        setFormData(
            prev => ({
                ...prev,

                [name]:
                    value,

                /*
                |--------------------------------------------------------------------------
                | Cascade Resets
                |--------------------------------------------------------------------------
                */

                ...(name ===
                "orgId"
                    ?
                    {
                        trailId:
                            "",

                        cohortId:
                            "",

                        sessionId:
                            "",

                        challengeId:
                            "",

                        selectedUserIds:
                            [],
                    }
                    :
                    {}),

                ...(name ===
                "trailId"
                    ?
                    {
                        cohortId:
                            "",

                        sessionId:
                            "",

                        challengeId:
                            "",

                        selectedUserIds:
                            [],
                    }
                    :
                    {}),

                ...(name ===
                "cohortId"
                    ?
                    {
                        selectedUserIds:
                            [],
                    }
                    :
                    {}),

                ...(name ===
                "sessionId"
                    ?
                    {
                        challengeId:
                            "",
                    }
                    :
                    {}),

                ...(name ===
                "audienceType" &&
                value ===
                    "AllCohortUsers"
                    ?
                    {
                        selectedUserIds:
                            [],
                    }
                    :
                    {}),
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 25 - Toggle Selected Cohort User
    |--------------------------------------------------------------------------
    */

    const handleSelectedUserToggle = (
        userId
    ) => {
        const exists =
            formData
                .selectedUserIds
                .some(
                    selectedId =>
                        selectedId
                            ?.toString() ===
                        userId
                            ?.toString()
                )

        const updatedUserIds =
            exists
                ?
                formData
                    .selectedUserIds
                    .filter(
                        selectedId =>
                            selectedId
                                ?.toString() !==
                            userId
                                ?.toString()
                    )
                :
                [
                    ...formData
                        .selectedUserIds,

                    userId,
                ]

        setFormData(
            prev => ({
                ...prev,

                selectedUserIds:
                    updatedUserIds,
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 26 - Select All Cohort Users
    |--------------------------------------------------------------------------
    */

    const handleSelectAllUsers = () => {
        setFormData(
            prev => ({
                ...prev,

                selectedUserIds:
                    eligibleCohortUsers
                        .map(
                            item =>
                                getRefId(
                                    item
                                        ?.userId
                                )
                        )
                        .filter(
                            Boolean
                        ),
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 27 - Clear Selected Users
    |--------------------------------------------------------------------------
    */

    const handleClearSelectedUsers = () => {
        setFormData(
            prev => ({
                ...prev,

                selectedUserIds:
                    [],
            })
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 28 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (
            e
        ) => {
            e.preventDefault()

            /*
            |--------------------------------------------------------------------------
            | Required Assignment Context
            |--------------------------------------------------------------------------
            */

            if (
                !formData.orgId ||
                !formData.trailId ||
                !formData.cohortId ||
                !formData.sessionId ||
                !formData.challengeId
            ) {
                alert(
                    "Organization, Trail, Cohort, Session and Challenge are required."
                )

                return
            }

            /*
            |--------------------------------------------------------------------------
            | Prevent Create Screen From Updating An Existing Assignment
            |--------------------------------------------------------------------------
            */

            if (
                matchingExistingAssignment
            ) {
                alert(
                    "An explicit assignment already exists for this Organization, Trail, Cohort, Session and Challenge. Use Update instead."
                )

                return
            }

            /*
            |--------------------------------------------------------------------------
            | Selected Users Validation
            |--------------------------------------------------------------------------
            */

            if (
                formData
                    .audienceType ===
                    "SelectedUsers" &&
                formData
                    .selectedUserIds
                    .length ===
                    0
            ) {
                alert(
                    "Select at least one cohort user."
                )

                return
            }

            /*
            |--------------------------------------------------------------------------
            | Availability Validation
            |--------------------------------------------------------------------------
            */

            if (
                formData
                    .availableFrom &&
                formData
                    .availableUntil &&
                new Date(
                    formData
                        .availableUntil
                ) <
                new Date(
                    formData
                        .availableFrom
                )
            ) {
                alert(
                    "Available Until cannot be earlier than Available From."
                )

                return
            }

            const assignmentData = {
                orgId:
                    formData
                        .orgId,

                trailId:
                    formData
                        .trailId,

                sessionId:
                    formData
                        .sessionId,

                challengeId:
                    formData
                        .challengeId,

                cohortId:
                    formData
                        .cohortId,

                audienceType:
                    formData
                        .audienceType,

                selectedUserIds:
                    formData
                        .audienceType ===
                        "SelectedUsers"
                        ?
                        formData
                            .selectedUserIds
                        :
                        [],

                assignmentStatus:
                    formData
                        .assignmentStatus,

                availableFrom:
                    formData
                        .availableFrom ||
                    undefined,

                availableUntil:
                    formData
                        .availableUntil ||
                    undefined,

                assignmentReason:
                    formData
                        .assignmentReason,

                active:
                    formData
                        .active,
            }

            try {
                const response =
                    await upsertOrgTrailSessionChallengeAssignment(
                        assignmentData
                    )
                        .unwrap()

                alert(
                    response?.message ||
                    "Session Challenge assignment created successfully"
                )
            } catch (
                submitError
            ) {
                console.error(
                    submitError
                )

                alert(
                    submitError
                        ?.data
                        ?.message ||
                    "Failed to create Session Challenge assignment"
                )
            }
        }

    /*
    |--------------------------------------------------------------------------
    | 29 - Cancel
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {
        if (
            setActiveAdminView
        ) {
            setActiveAdminView(
                "orgTrailSessionChallengeAssignments"
            )
        } else {
            navigate(
                "/orgTrailSessionChallengeAssignments"
            )
        }
    }

    /*
    |--------------------------------------------------------------------------
    | 30 - Clear
    |--------------------------------------------------------------------------
    */

    const handleClear = () => {
        setFormData(
            initialFormState
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 31 - Organization Options
    |--------------------------------------------------------------------------
    */

    const organizationOptions =
        organizationList.map(
            organization => {
                const id =
                    getRefId(
                        organization
                    )

                return (
                    <option
                        key={
                            id
                        }

                        value={
                            id
                        }
                    >
                        {
                            organization
                                ?.organizationName
                        }
                    </option>
                )
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 32 - Trail Options
    |--------------------------------------------------------------------------
    */

    const trailOptions =
        trailList.map(
            trail => {
                const id =
                    getRefId(
                        trail
                    )

                return (
                    <option
                        key={
                            id
                        }

                        value={
                            id
                        }
                    >
                        {
                            trail
                                ?.trailName
                        }
                    </option>
                )
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 33 - Cohort Options
    |--------------------------------------------------------------------------
    */

    const cohortOptions =
        filteredCohorts.map(
            cohort => {
                const id =
                    getRefId(
                        cohort
                    )

                return (
                    <option
                        key={
                            id
                        }

                        value={
                            id
                        }
                    >
                        {
                            cohort
                                ?.cohortName ||
                            cohort
                                ?.cohortCode
                        }
                    </option>
                )
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 34 - Session Options
    |--------------------------------------------------------------------------
    */

    const sessionOptions =
        filteredSessions.map(
            session => {
                const id =
                    getRefId(
                        session
                    )

                return (
                    <option
                        key={
                            id
                        }

                        value={
                            id
                        }
                    >
                        {
                            session
                                ?.sessionSequence
                                ?
                                `${session.sessionSequence} - `
                                :
                                ""
                        }
                        {
                            session
                                ?.sessionName
                        }
                    </option>
                )
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 35 - Challenge Options
    |--------------------------------------------------------------------------
    */

    const challengeOptions =
        filteredSessionChallenges.map(
            challenge => {
                const id =
                    getRefId(
                        challenge
                    )

                return (
                    <option
                        key={
                            id
                        }

                        value={
                            id
                        }
                    >
                        {
                            challenge
                                ?.challengeName
                        }
                    </option>
                )
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 36 - Render
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black"
        >

            {
                isLoading &&
                (
                    <PulseLoader
                        color={
                            "#ea580c"
                        }
                    />
                )
            }

            {
                isError &&
                (
                    <p
                        className="text-orange-700 font-semi-bold py-2"
                    >
                        {
                            error
                                ?.data
                                ?.message
                        }
                    </p>
                )
            }

            <div
                className="bg-white p-1 m-1 rounded"
            >

                <p
                    className="p-1 m-1 font-bold font-poppins text-xl"
                >
                    Add a Session Challenge Assignment
                </p>

                <div
                    className="bg-gray-200 rounded m-1 p-1"
                >

                    <form
                        onSubmit={
                            handleSubmit
                        }

                        className="m-1 p-1 text-xs font-poppins"
                    >

                        {/* 1 - Organization */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Organization
                            </p>

                            <select
                                name="orgId"

                                value={
                                    formData
                                        .orgId
                                }

                                onChange={
                                    handleChange
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            >
                                <option
                                    value=""
                                >
                                    Select Organization
                                </option>

                                {
                                    organizationOptions
                                }
                            </select>
                        </div>

                        {/* 2 - Trail */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Trail
                            </p>

                            <select
                                name="trailId"

                                value={
                                    formData
                                        .trailId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .orgId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >
                                <option
                                    value=""
                                >
                                    Select Trail
                                </option>

                                {
                                    trailOptions
                                }
                            </select>
                        </div>

                        {/* 3 - Cohort */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <div
                                className="flex justify-between items-center"
                            >
                                <div>
                                    <p
                                        className="font-bold text-xs m-1"
                                    >
                                        Cohort
                                    </p>

                                    <p
                                        className="text-[8px] m-1 italic text-gray-600"
                                    >
                                        The selected cohort defines the user population for this challenge assignment.
                                    </p>
                                </div>

                                {
                                    formData
                                        .cohortId &&
                                    (
                                        <p
                                            className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                        >
                                            Users:{" "}
                                            {
                                                eligibleCohortUsers
                                                    .length
                                            }
                                        </p>
                                    )
                                }
                            </div>

                            <select
                                name="cohortId"

                                value={
                                    formData
                                        .cohortId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .orgId ||
                                    !formData
                                        .trailId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >
                                <option
                                    value=""
                                >
                                    Select Cohort
                                </option>

                                {
                                    cohortOptions
                                }
                            </select>

                            {
                                formData
                                    .orgId &&
                                formData
                                    .trailId &&
                                filteredCohorts
                                    .length ===
                                    0 &&
                                (
                                    <div
                                        className="bg-gray-100 rounded m-1 p-2"
                                    >
                                        <p
                                            className="text-[10px] text-gray-600"
                                        >
                                            No active cohorts found for the selected organization and trail.
                                        </p>
                                    </div>
                                )
                            }
                        </div>

                        {/* 4 - Session */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session
                            </p>

                            <select
                                name="sessionId"

                                value={
                                    formData
                                        .sessionId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .trailId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >
                                <option
                                    value=""
                                >
                                    Select Session
                                </option>

                                {
                                    sessionOptions
                                }
                            </select>
                        </div>

                        {/* 5 - Session Challenge */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session Challenge
                            </p>

                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                Only challenges already attached to the selected Session are available.
                            </p>

                            <select
                                name="challengeId"

                                value={
                                    formData
                                        .challengeId
                                }

                                onChange={
                                    handleChange
                                }

                                disabled={
                                    !formData
                                        .sessionId
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                            >
                                <option
                                    value=""
                                >
                                    Select Challenge
                                </option>

                                {
                                    challengeOptions
                                }
                            </select>

                            {
                                formData
                                    .sessionId &&
                                filteredSessionChallenges
                                    .length ===
                                    0 &&
                                (
                                    <div
                                        className="bg-gray-100 rounded m-1 p-2"
                                    >
                                        <p
                                            className="text-[10px] text-gray-600"
                                        >
                                            No challenges are attached to this Session.
                                        </p>
                                    </div>
                                )
                            }

                            {
                                matchingExistingAssignment &&
                                (
                                    <div
                                        className="bg-orange-50 rounded m-1 p-2"
                                    >
                                        <p
                                            className="text-[10px] text-orange-700"
                                        >
                                            An explicit assignment already exists for this cohort and challenge. Use Update instead.
                                        </p>
                                    </div>
                                )
                            }
                        </div>

                        {/* 6 - Audience */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Challenge Audience
                            </p>

                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                Choose whether the challenge should be available to the full cohort or only selected users.
                            </p>

                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-2 m-1"
                            >

                                <label
                                    className={`rounded p-3 cursor-pointer border ${
                                        formData
                                            .audienceType ===
                                            "AllCohortUsers"
                                            ?
                                            "border-orange-600 bg-orange-50"
                                            :
                                            "border-gray-200 bg-gray-100"
                                    }`}
                                >
                                    <div
                                        className="flex justify-start items-center"
                                    >
                                        <input
                                            type="radio"

                                            name="audienceType"

                                            value="AllCohortUsers"

                                            checked={
                                                formData
                                                    .audienceType ===
                                                "AllCohortUsers"
                                            }

                                            onChange={
                                                handleChange
                                            }

                                            className="m-1"
                                        />

                                        <div
                                            className="mx-1"
                                        >
                                            <p
                                                className="text-xs font-bold"
                                            >
                                                All Cohort Users
                                            </p>

                                            <p
                                                className="text-[9px] text-gray-500"
                                            >
                                                Everyone currently eligible in the selected cohort.
                                            </p>
                                        </div>
                                    </div>
                                </label>

                                <label
                                    className={`rounded p-3 cursor-pointer border ${
                                        formData
                                            .audienceType ===
                                            "SelectedUsers"
                                            ?
                                            "border-orange-600 bg-orange-50"
                                            :
                                            "border-gray-200 bg-gray-100"
                                    }`}
                                >
                                    <div
                                        className="flex justify-start items-center"
                                    >
                                        <input
                                            type="radio"

                                            name="audienceType"

                                            value="SelectedUsers"

                                            checked={
                                                formData
                                                    .audienceType ===
                                                "SelectedUsers"
                                            }

                                            onChange={
                                                handleChange
                                            }

                                            className="m-1"
                                        />

                                        <div
                                            className="mx-1"
                                        >
                                            <p
                                                className="text-xs font-bold"
                                            >
                                                Selected Users
                                            </p>

                                            <p
                                                className="text-[9px] text-gray-500"
                                            >
                                                Meter this challenge to only selected users from the cohort.
                                            </p>
                                        </div>
                                    </div>
                                </label>

                            </div>

                            {
                                formData
                                    .cohortId &&
                                (
                                    <p
                                        className="text-[10px] bg-gray-100 text-gray-600 rounded m-1 p-2"
                                    >
                                        Cohort population:{" "}
                                        <span
                                            className="font-bold"
                                        >
                                            {
                                                eligibleCohortUsers
                                                    .length
                                            }
                                        </span>
                                    </p>
                                )
                            }
                        </div>

                        {/* 7 - Selected Users */}

                        {
                            formData
                                .audienceType ===
                                "SelectedUsers" &&
                            (
                                <div
                                    className="bg-white rounded m-1 p-2"
                                >

                                    <div
                                        className="flex justify-between items-center"
                                    >

                                        <div>
                                            <p
                                                className="font-bold text-xs m-1"
                                            >
                                                Selected Cohort Users
                                            </p>

                                            <p
                                                className="text-[8px] m-1 italic text-gray-600"
                                            >
                                                Only users from the selected cohort can be assigned.
                                            </p>
                                        </div>

                                        <p
                                            className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                        >
                                            Selected:{" "}
                                            {
                                                formData
                                                    .selectedUserIds
                                                    .length
                                            }
                                            {" / "}
                                            {
                                                eligibleCohortUsers
                                                    .length
                                            }
                                        </p>
                                    </div>

                                    {
                                        !formData
                                            .cohortId &&
                                        (
                                            <div
                                                className="bg-gray-100 rounded m-1 p-2"
                                            >
                                                <p
                                                    className="text-[10px] text-gray-600"
                                                >
                                                    Select a cohort first.
                                                </p>
                                            </div>
                                        )
                                    }

                                    {
                                        formData
                                            .cohortId &&
                                        (
                                            <>
                                                <div
                                                    className="flex justify-end items-center m-1"
                                                >
                                                    <button
                                                        type="button"

                                                        onClick={
                                                            handleSelectAllUsers
                                                        }

                                                        className="text-[9px] border border-orange-600 text-orange-600 bg-white rounded-full px-3 py-1 hover:bg-orange-600 hover:text-white"
                                                    >
                                                        Select All
                                                    </button>

                                                    <button
                                                        type="button"

                                                        onClick={
                                                            handleClearSelectedUsers
                                                        }

                                                        className="text-[9px] border border-gray-400 text-gray-600 bg-white rounded-full mx-1 px-3 py-1 hover:bg-gray-600 hover:text-white"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>

                                                <div
                                                    className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-96 overflow-y-auto"
                                                >
                                                    {
                                                        eligibleCohortUsers.map(
                                                            (
                                                                cohortUserItem,
                                                                index
                                                            ) => {
                                                                const user =
                                                                    cohortUserItem
                                                                        ?.userId

                                                                const userId =
                                                                    getRefId(
                                                                        user
                                                                    )

                                                                const isChecked =
                                                                    formData
                                                                        .selectedUserIds
                                                                        .some(
                                                                            selectedId =>
                                                                                selectedId
                                                                                    ?.toString() ===
                                                                                userId
                                                                                    ?.toString()
                                                                        )

                                                                return (
                                                                    <div
                                                                        key={
                                                                            userId ||
                                                                            index
                                                                        }

                                                                        className={`flex justify-between items-center rounded m-1 p-2 ${
                                                                            isChecked
                                                                                ?
                                                                                "bg-orange-50 border border-orange-300"
                                                                                :
                                                                                "bg-gray-100"
                                                                        }`}
                                                                    >

                                                                        <div
                                                                            className="flex justify-start items-center"
                                                                        >
                                                                            <input
                                                                                type="checkbox"

                                                                                checked={
                                                                                    isChecked
                                                                                }

                                                                                onChange={
                                                                                    () =>
                                                                                        handleSelectedUserToggle(
                                                                                            userId
                                                                                        )
                                                                                }

                                                                                className="m-1"
                                                                            />

                                                                            <div
                                                                                className="mx-1"
                                                                            >
                                                                                <p
                                                                                    className="text-xs font-bold text-gray-700"
                                                                                >
                                                                                    {
                                                                                        getDisplayName(
                                                                                            user
                                                                                        )
                                                                                    }
                                                                                </p>

                                                                                {
                                                                                    user
                                                                                        ?.email &&
                                                                                    (
                                                                                        <p
                                                                                            className="text-[9px] text-gray-500"
                                                                                        >
                                                                                            {
                                                                                                user
                                                                                                    .email
                                                                                            }
                                                                                        </p>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        </div>

                                                                        {
                                                                            cohortUserItem
                                                                                ?.userCohortStatus &&
                                                                            (
                                                                                <p
                                                                                    className="text-[8px] bg-white text-gray-600 rounded-full mx-1 px-2 py-1"
                                                                                >
                                                                                    {
                                                                                        cohortUserItem
                                                                                            .userCohortStatus
                                                                                    }
                                                                                </p>
                                                                            )
                                                                        }
                                                                    </div>
                                                                )
                                                            }
                                                        )
                                                    }
                                                </div>

                                                {
                                                    eligibleCohortUsers
                                                        .length ===
                                                        0 &&
                                                    (
                                                        <div
                                                            className="bg-gray-100 rounded m-1 p-2"
                                                        >
                                                            <p
                                                                className="text-[10px] text-gray-600"
                                                            >
                                                                No eligible users found in this cohort.
                                                            </p>
                                                        </div>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                </div>
                            )
                        }

                        {/* 8 - Assignment Status */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <p
                                className="font-bold text-xs m-1"
                            >
                                Assignment Status
                            </p>

                            <select
                                name="assignmentStatus"

                                value={
                                    formData
                                        .assignmentStatus
                                }

                                onChange={
                                    handleChange
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            >
                                <option
                                    value="Planned"
                                >
                                    Planned
                                </option>

                                <option
                                    value="Open"
                                >
                                    Open
                                </option>

                                <option
                                    value="Closed"
                                >
                                    Closed
                                </option>

                                <option
                                    value="Archived"
                                >
                                    Archived
                                </option>
                            </select>
                        </div>

                        {/* 9 - Availability */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Availability
                            </p>

                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                Optional. Leave blank when the assignment has no specific availability window.
                            </p>

                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-2"
                            >

                                <div>
                                    <p
                                        className="text-[10px] m-1"
                                    >
                                        Available From
                                    </p>

                                    <input
                                        type="datetime-local"

                                        name="availableFrom"

                                        value={
                                            formData
                                                .availableFrom
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />
                                </div>

                                <div>
                                    <p
                                        className="text-[10px] m-1"
                                    >
                                        Available Until
                                    </p>

                                    <input
                                        type="datetime-local"

                                        name="availableUntil"

                                        value={
                                            formData
                                                .availableUntil
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />
                                </div>

                            </div>
                        </div>

                        {/* 10 - Assignment Reason */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Assignment Reason
                            </p>

                            <p
                                className="text-[8px] m-1 italic text-gray-600"
                            >
                                This reason becomes part of the encrypted assignment change history.
                            </p>

                            <textarea
                                name="assignmentReason"

                                rows="4"

                                placeholder="Why is this challenge being assigned this way?"

                                value={
                                    formData
                                        .assignmentReason
                                }

                                onChange={
                                    handleChange
                                }

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />
                        </div>

                        {/* 11 - Active */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >
                            <div
                                className="flex justify-start items-center bg-gray-100 rounded m-1 p-2"
                            >
                                <input
                                    type="checkbox"

                                    name="active"

                                    checked={
                                        formData
                                            .active
                                    }

                                    onChange={
                                        handleChange
                                    }

                                    className="m-1"
                                />

                                <p
                                    className="text-xs m-1"
                                >
                                    Active
                                </p>
                            </div>
                        </div>

                        {/* 12 - Actions */}

                        <div
                            id="actions"

                            className="bg-white m-1 px-1 py-3 rounded"
                        >

                            <EtBtnFormBasic
                                name={
                                    "Create"
                                }

                                type={
                                    "create"
                                }

                                handleClick={
                                    handleSubmit
                                }
                            />

                            <EtBtnFormBasic
                                name={
                                    "Clear"
                                }

                                type={
                                    "clear"
                                }

                                handleClick={
                                    handleClear
                                }
                            />

                            <EtBtnFormBasic
                                name={
                                    "Cancel"
                                }

                                type={
                                    "cancel"
                                }

                                handleClick={
                                    handleCancel
                                }
                            />

                        </div>

                    </form>
                </div>
            </div>
        </div>
    )
}

export default EtCreateOrgTrailSessionChallengeAssignment